import { Phone, ArrowRight, Leaf, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { COMPANY, LICENCES } from "@/data/siteContent";
import Logo from "@/components/brand/Logo";
import WaterBadge from "@/components/brand/WaterBadge";

const HERO_IMG =
  "https://images.pexels.com/photos/12285892/pexels-photo-12285892.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-[96px] md:pt-[128px] pb-16 md:pb-24 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#29ABE2]/20 blur-3xl" />
        <div className="absolute top-40 -left-24 h-[420px] w-[420px] rounded-full bg-[#7BC7A3]/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/3 h-[360px] w-[360px] rounded-full bg-[#E3F5EA] dark:bg-[#12331E] blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 eco-reveal">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-[#7BC7A3]/70 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#1F8A3A]"
            >
              <Leaf className="h-3.5 w-3.5" /> Toronto & GTA
            </span>
            <h1
              data-testid="hero-heading"
              className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#0E2A17] dark:text-[#F2FAF4] leading-[1.02]"
            >
              Mold removed.
              <br />
              <span className="text-[#1F8A3A]">Air restored.</span>
              <br />
              <span className="text-[#29ABE2]">Home protected.</span>
            </h1>
            <p
              data-testid="hero-subheading"
              className="mt-6 max-w-xl text-base md:text-lg text-[#3F5549] dark:text-[#B9CDC0] leading-relaxed"
            >
              Ecomold Clean is a licensed, insured mold inspection and
              remediation team serving homeowners across Toronto and the
              Greater Toronto Area. Careful, code-aligned work — done right
              the first time.
            </p>

            <div
              data-testid="hero-licence-badges"
              className="mt-7 inline-flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-[#1F8A3A]/25 bg-[#E3F5EA] dark:bg-[#12331E] px-5 py-3"
            >
              <BadgeCheck className="h-5 w-5 text-[#1F8A3A] shrink-0" />
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1F8A3A]">
                Certified
              </span>
              <span className="mx-1 h-4 w-px bg-[#1F8A3A]/30" />
              {LICENCES.map((l, i) => (
                <span key={l.name} className="flex items-center gap-2">
                  <span
                    data-testid={`licence-${l.name.toLowerCase()}`}
                    title={l.note}
                    className={`font-display text-base md:text-lg font-extrabold tracking-wide ${
                      l.gold
                        ? "bg-gradient-to-b from-[#E9B949] via-[#C79514] to-[#8C640A] bg-clip-text text-transparent drop-shadow-[0_1px_0_rgba(201,149,20,0.25)]"
                        : "text-[#0E2A17] dark:text-[#F2FAF4]"
                    }`}
                  >
                    {l.name}
                    {l.suffix && (
                      <span className="ml-1 text-xs font-semibold text-[#1F8A3A] align-middle">
                        {l.suffix}
                      </span>
                    )}
                  </span>
                  {i < LICENCES.length - 1 && (
                    <span className="text-[#1F8A3A]/40">·</span>
                  )}
                </span>
              ))}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#29ABE2] px-7 py-4 text-base font-semibold text-white shadow-sm hover:bg-[#1A8ABF] hover:-translate-y-0.5 transition-all"
              >
                Book an Inspection
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={COMPANY.phoneHref}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1F8A3A] px-7 py-4 text-base font-semibold text-white hover:bg-[#0F5C24] transition-all"
              >
                <Phone className="h-4 w-4" />
                Call {COMPANY.phone}
              </a>
            </div>

            <p
              data-testid="hero-waiver-note"
              className="mt-4 text-sm text-[#3F5549] dark:text-[#B9CDC0] max-w-xl leading-relaxed"
            >
              <span className="font-semibold text-[#1F8A3A]">
                Inspection fee waived
              </span>{" "}
              when mold is found and you book the remediation on the same
              visit — you pay only for the cleaning job.
            </p>

            <WaterBadge
              data-testid="hero-water-badge"
              className="mt-6 max-w-xl"
            />

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4 text-sm text-[#3F5549] dark:text-[#B9CDC0]">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#1F8A3A]" />
                Licensed & insured
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#29ABE2]" />
                24-hour response in GTA
              </div>
              <div className="flex items-center gap-2">
                <Leaf className="h-4 w-4 text-[#1F8A3A]" />
                Eco-safe protocols
              </div>
            </div>
          </div>

          <div
            className="lg:col-span-5 eco-reveal"
            style={{ animationDelay: "120ms" }}
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-[28px] bg-[#E3F5EA] dark:bg-[#12331E] -z-10" />
              <img
                src={HERO_IMG}
                alt="Clean and healthy home interior"
                data-testid="hero-image"
                className="w-full h-[440px] md:h-[520px] object-cover rounded-3xl shadow-xl"
                loading="eager"
              />

              {/* 3D floating brand mark */}
              <div
                data-testid="hero-logo-3d"
                className="absolute -top-10 -right-4 md:-top-16 md:-right-8 pointer-events-none"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#7BC7A3]/40 via-[#29ABE2]/25 to-transparent blur-2xl -z-10 rounded-full" />
                  <Logo
                    size={180}
                    withHoverTilt={true}
                    imgClassName="eco-logo-float"
                    data-testid="hero-logo-image"
                  />
                </div>
              </div>

              {/* Certification floating card */}
              <div
                data-testid="hero-floating-card"
                className="absolute -bottom-6 -left-6 md:-left-10 bg-white dark:bg-[#0A2513] rounded-2xl shadow-xl border border-[#E3F5EA] dark:border-[#1A3925] p-5 w-64"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#1F8A3A]/10 flex items-center justify-center">
                    <BadgeCheck className="h-5 w-5 text-[#1F8A3A]" />
                  </div>
                  <div>
                    <div className="text-xs text-[#3F5549] dark:text-[#B9CDC0]">Certified</div>
                    <div className="font-semibold text-[#0E2A17] dark:text-[#F2FAF4]">
                      <span className="bg-gradient-to-b from-[#E9B949] via-[#C79514] to-[#8C640A] bg-clip-text text-transparent font-extrabold">
                        IICRC
                      </span>{" "}
                      · AMRT · WHMIS · WRT
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-[#3F5549] dark:text-[#B9CDC0] leading-relaxed">
                  Every job follows industry-standard remediation protocols.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
